import React from "react";
import { Lightbulb, Plus } from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import Button from "../ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import Checkbox from "../ui/Checkbox";
import { useLights, useSceneActions, useSceneStore } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  DEFAULT_VIEWPORT_LIGHTS,
  getViewportLightingOptions,
  resetViewportLightingOptions,
  subscribeViewportLightingOptions,
  updateViewportLight,
} from "../../utils/defaultViewportLights";
import { focusNewEntityInInspector } from "../../utils/focusNewEntityInInspector";
import { getSceneHdriPath } from "../../utils/sceneHdri";
import {
  getSceneHdriBackgroundVisible,
  setSceneHdriBackgroundVisible,
  subscribeSceneHdriBackgroundVisibility,
} from "../../utils/sceneHdriBackgroundVisibility";

const EMPTY_OBJECT = Object.freeze({});
const labelClass = "text-[10px] panel-text-helper uppercase";
const inputClass =
  "w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-100 text-xs outline-none focus:border-gray-500";

const sortByName = (a, b) => String(a?.name || "").localeCompare(String(b?.name || ""));

const numericValue = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

function SliderField({ label, value, min = 0, max = 5, step = 0.05, onChange }) {
  return (
    <label className="space-y-1">
      <div className="flex items-center justify-between gap-3">
        <span className={labelClass}>{label}</span>
        <span className="text-xs tabular-nums text-gray-300">{Number(value).toFixed(2)}</span>
      </div>
      <input
        className="h-2 w-full slider"
        style={{ accentColor: "#f97316" }}
        type="range"
        min={min}
        max={max}
        step={step}
        value={Number.isFinite(Number(value)) ? value : 0}
        onChange={(event) => onChange(numericValue(event.target.value, value))}
      />
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <label className="space-y-1">
      <span className={labelClass}>{label}</span>
      <div className="flex items-center gap-2">
        <input
          className="h-8 w-10 cursor-pointer rounded border border-gray-700 bg-gray-800"
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <input
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </label>
  );
}

function ViewportLightCard({ light, values, onUpdate }) {
  const colorRows =
    light.type === "hemisphere"
      ? [
          { key: "skyColor", color: values.skyColor, label: "Sky" },
          { key: "groundColor", color: values.groundColor, label: "Underside" },
        ]
      : [{ key: "color", color: values.color, label: "Color" }];
  return (
    <div
      className="rounded border border-gray-700 p-3"
      style={{ backgroundColor: "var(--panel-surface)" }}
    >
      <div className="truncate text-sm font-medium panel-text-primary">
        {light.label || light.name}
      </div>
      <div className="mt-3 space-y-3">
        <SliderField
          label="Intensity"
          value={values.intensity}
          onChange={(intensity) => onUpdate({ intensity })}
        />
        {colorRows.map((row) => (
          <ColorField
            key={row.key}
            label={row.label}
            value={row.color}
            onChange={(color) => onUpdate({ [row.key]: color })}
          />
        ))}
      </div>
    </div>
  );
}

function EnvironmentLightingSection({
  background,
  backgroundLibrary,
  hdriVisible,
  onToggleHdriVisible,
}) {
  const backgroundAsset =
    background?.type === "asset" && background.assetId
      ? backgroundLibrary?.[background.assetId] || null
      : null;
  const sourceLabel =
    background?.type === "color"
      ? "Plain color background"
      : background?.type === "asset" && backgroundAsset
        ? backgroundAsset.name || backgroundAsset.fileName || "Uploaded environment"
        : "Built-in studio HDRI";
  const status = background?.type === "color" ? "disabled" : hdriVisible ? "visible" : "hidden";
  const detail =
    background?.type === "color"
      ? "HDRI environment lighting is off while a plain color background is selected."
      : background?.type === "asset"
        ? hdriVisible
          ? "Uploaded backgrounds are visible and used as the viewport environment map when supported."
          : "Uploaded environment lighting remains active while the background image is hidden."
        : hdriVisible
          ? getSceneHdriPath()
          : "Built-in HDRI lighting remains active while the background image is hidden.";

  return (
    <div
      className="rounded border border-gray-700 p-3"
      style={{ backgroundColor: "var(--panel-surface)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium panel-text-primary">HDRI environment</div>
          <div className="mt-0.5 text-xs panel-text-helper">{sourceLabel}</div>
        </div>
        <div className="rounded bg-gray-800 px-2 py-0.5 text-xs panel-text-helper">{status}</div>
      </div>
      <div className="mt-3">
        <Checkbox
          label="Show HDRI"
          checked={hdriVisible}
          disabled={background?.type === "color"}
          onChange={(event) => onToggleHdriVisible(event.target.checked)}
          className="w-auto px-0 py-0 text-xs text-gray-300"
        />
      </div>
      <div className="mt-3 break-words text-xs panel-text-helper">{detail}</div>
    </div>
  );
}

function LightsPanel({ open, onClose, titleIcon, mobilePinPanelId }) {
  const lights = useLights() ?? EMPTY_OBJECT;
  const background = useSceneStore((state) => state.background);
  const backgroundLibrary = useSceneStore((state) => state.backgroundLibrary);
  const sceneActions = useSceneActions();
  const selectedLight = useSelectionStore((state) => state.selectedLight);
  const selectLight = useSelectionStore((state) => state.selectLight);
  const hdriBackgroundVisible = React.useSyncExternalStore(
    subscribeSceneHdriBackgroundVisibility,
    getSceneHdriBackgroundVisible,
    getSceneHdriBackgroundVisible
  );
  const viewportLighting = React.useSyncExternalStore(
    subscribeViewportLightingOptions,
    getViewportLightingOptions,
    getViewportLightingOptions
  );

  const lightList = React.useMemo(
    () => Object.values(lights).filter(Boolean).sort(sortByName),
    [lights]
  );
  const hdriVisible = background?.type !== "color" && hdriBackgroundVisible;

  const focusLight = React.useCallback(
    (lightUuid) => {
      if (!lightUuid) return;
      focusNewEntityInInspector("sceneTree", () => {
        if (useSelectionStore.getState().selectedLight !== lightUuid) {
          selectLight(lightUuid);
        }
      });
    },
    [selectLight]
  );

  const addLight = React.useCallback(() => {
    const uuid = sceneActions.addLight({
      name: "light",
      kind: "fixed",
      position: [0, 0, 2],
      direction: [0, 0, -1],
      type: "spot",
      active: true,
      castshadow: true,
      diffuse: [0.7, 0.7, 0.7],
      ambient: [0, 0, 0],
      specular: [0.3, 0.3, 0.3],
      range: 10,
      cutoff: 45,
      exponent: 10,
      attenuation: [1, 0, 0],
    });
    focusLight(uuid);
  }, [focusLight, sceneActions]);

  const toggleHdriVisible = React.useCallback((visible) => {
    setSceneHdriBackgroundVisible(visible);
  }, []);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Lights"
      titleIcon={titleIcon || Lightbulb}
      mobilePinPanelId={mobilePinPanelId}
      initialSize={{ width: 520, height: 680 }}
      minWidth={360}
      minHeight={440}
    >
      <div
        className="flex h-full min-h-0 flex-col panel-text-primary"
        style={{ backgroundColor: "var(--panel-surface)" }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Collapsible defaultOpen className="border-b border-gray-800 px-4 py-2">
            <CollapsibleTrigger
              className="text-sm font-medium text-gray-200"
              actions={
                <Button size="small" variant="ghost" onClick={resetViewportLightingOptions}>
                  Reset
                </Button>
              }
            >
              <span>Viewport Lights</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid gap-2 md:grid-cols-2">
                <ViewportLightCard
                  light={DEFAULT_VIEWPORT_LIGHTS.ambient}
                  values={viewportLighting.ambient}
                  onUpdate={(patch) => updateViewportLight("ambient", patch)}
                />
                <ViewportLightCard
                  light={DEFAULT_VIEWPORT_LIGHTS.hemisphere}
                  values={viewportLighting.hemisphere}
                  onUpdate={(patch) => updateViewportLight("hemisphere", patch)}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible defaultOpen className="border-b border-gray-800 px-4 py-2">
            <CollapsibleTrigger className="text-sm font-medium text-gray-200">
              <span>Environment Lighting</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <EnvironmentLightingSection
                background={background}
                backgroundLibrary={backgroundLibrary}
                hdriVisible={hdriVisible}
                onToggleHdriVisible={toggleHdriVisible}
              />
            </CollapsibleContent>
          </Collapsible>

          <Collapsible defaultOpen className="px-4 py-2">
            <CollapsibleTrigger
              className="text-sm font-medium text-gray-200"
              actions={
                <Button size="small" variant="secondary" onClick={addLight}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add
                </Button>
              }
            >
              <span>Scene Lights ({lightList.length})</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="overflow-hidden rounded border border-gray-800">
                {lightList.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">No scene lights.</div>
                ) : (
                  <div className="space-y-1 p-2">
                    {lightList.map((light) => (
                      <button
                        key={light.uuid}
                        type="button"
                        className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm ${
                          light.uuid === selectedLight
                            ? "bg-[var(--panel-surface-hover)] text-gray-100"
                            : "panel-text-helper hover:bg-[var(--panel-surface-hover)] hover:text-gray-100"
                        }`}
                        onClick={() => focusLight(light.uuid)}
                      >
                        <Lightbulb className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{light.name || light.uuid}</span>
                        <span className="shrink-0 rounded bg-gray-800 px-1.5 py-0.5 text-[10px] uppercase text-gray-400">
                          {light.type || "spot"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </DraggablePanel>
  );
}

export default LightsPanel;
